import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { element, getSlots } from '../../global/ui-helpers';
import { NullOr } from '../../global/utils.type';
import { UIObserverElement } from '../ui-observer-element';
import type { UIWidgetSlot, UIWidgetSlots } from './ui-widget.type';
import styles from './ui-widget.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIWidget
 * @element ui-widget
 * @classdesc Represents a class for <code>ui-widget</code> element.
 * @property {string} [heading=""] {@attr heading} Main heading for widget.
 * @slot {@name top-left} Top left container of widget. E.g. for heading.
 * @slot {@name top-right} Top right container of widget. E.g. for assist/filter chips, action buttons etc.
 * @slot {@name middle} Middle container of widget. E.g. for asset list and other content components.
 * @slot {@name bottom} Bottom container of widget. E.g. for links, with vertical or horizontal stacking.
 * @slot
 * @example
 * <ui-widget heading="Your accounts">
 *   <ui-chips slot="top-right">
 *      <ui-chip label="Filter chip 1" layout="filter"></ui-chip>
 *      <ui-chip label="Filter chip 2" layout="filter"></ui-chip>
 *   </ui-chips>
 *   <ui-assets slot="middle" expandable>
 *     <ui-asset label="Main account" label-secondary="EE345678909870901345">
 *       <ui-badge layout="pin" color="pineapple">Default</ui-badge>
 *       <span slot="value"><ui-currency value="1200.48"></ui-currency> EUR</span>
 *     </ui-asset>
 *     <ui-asset
 *       label="Randy Lahey"
 *       label-secondary="EE323601345251978909"
 *       value-secondary="+2 currencies"
 *     >
 *       <span slot="value"><ui-currency value="12870.72"></ui-currency> EUR</span>
 *     </ui-asset>
 *     <ui-asset
 *       label="Credit card account"
 *       label-secondary="EE319781678251782328"
 *     >
 *       <span slot="value"><ui-currency value="900.35"></ui-currency> EUR</span>
 *     </ui-asset>
 *   </ui-assets>
 *   <ui-link slot="bottom" href="#" layout="primary">Summary statement</ui-link>
 * </ui-widget>
 */
@Component({
    tag: 'ui-widget',
    styles,
})
export class UIWidget extends UIObserverElement {
    /**
     * @type {UIWidgetSlots}
     */
    static readonly slots: UIWidgetSlots = {
        TopLeft: 'top-left',
        TopRight: 'top-right',
        Middle: 'middle',
        Bottom: 'bottom',
    };

    @Attribute(String) heading = '';

    @Child('.ui-widget__container') widgetContainer!: HTMLDivElement;
    @Child('.ui-widget__top-wrapper') topWrapper?: HTMLDivElement;
    @Child('.ui-widget__top-left-wrapper') topLeftWrapper?: HTMLDivElement;
    @Child('.ui-widget__top-right-wrapper') topRightWrapper?: HTMLDivElement;
    @Child('.ui-widget__middle-wrapper') middleWrapper?: HTMLDivElement;
    @Child('.ui-widget__bottom-wrapper') bottomWrapper?: HTMLDivElement;
    @Child('.ui-widget__content') contentWrapper?: HTMLDivElement;

    getObservedMutationOptions(): MutationObserverInit {
        return { childList: true, subtree: true };
    }

    /**
     * Extract children for the given slot into a proper wrapper.
     * @param {UIWidgetSlot} slot
     * @returns {HTMLDivElement | null}
     * @private
     */
    getFormattedSlot(slot: UIWidgetSlot): NullOr<HTMLDivElement> {
        const slotContent = getSlots(this, slot)[0];
        if (!slotContent) {
            return null;
        }

        slotContent?.removeAttribute('slot');
        return element<HTMLDivElement>`<div class="ui-widget__${slot}-wrapper">${slotContent}</div>`;
    }

    private createWidgetContainer() {
        if (!this.widgetContainer) {
            this.append(element`<div class="ui-widget__container"></div>`);
        }
    }

    private createTopContainer() {
        const topLeftSlot = this.getFormattedSlot(UIWidget.slots.TopLeft);
        const topRightSlot = this.getFormattedSlot(UIWidget.slots.TopRight);
        if (!topLeftSlot && !topRightSlot && !this.heading) {
            return;
        }

        let topWrapper = this.topWrapper;
        if (!topWrapper) {
            topWrapper = element<HTMLDivElement>`<div class="ui-widget__top-wrapper"></div>`;
            this.widgetContainer.insertAdjacentElement(
                'afterbegin',
                topWrapper
            );
        }

        let topInnerWrapper = this.querySelector(
            '.ui-widget__top-inner-wrapper'
        );
        if (!topInnerWrapper) {
            topInnerWrapper = element<HTMLDivElement>`<div class="ui-widget__top-inner-wrapper"></div>`;
            topWrapper.append(topInnerWrapper);
        }

        if (!this.topLeftWrapper) {
            if (topLeftSlot) {
                topInnerWrapper.append(topLeftSlot);
            } else {
                const content = this.heading
                    ? this.createElement({
                          tagName: 'h2',
                          classList: { 'ui-widget__heading': true },
                          children: [this.heading],
                      })
                    : null;
                const topLeftWrapper = element<HTMLDivElement>`<div class="ui-widget__top-left-wrapper">${content}</div>`;
                topInnerWrapper.append(topLeftWrapper);
            }
        }

        if (!this.topRightWrapper && topRightSlot) {
            topInnerWrapper.append(topRightSlot);
        }
    }

    private createContentContainer() {
        const nonSlotChildren = [...this.children].filter(
            (child) =>
                child !== this.widgetContainer && !child.hasAttribute('slot')
        );
        if (!nonSlotChildren.length) {
            return;
        }

        let contentContainer = this.contentWrapper;
        if (!contentContainer) {
            contentContainer = element<HTMLDivElement>`<div class="ui-widget__content"></div>`;
            const topSibling = this.middleWrapper || this.topWrapper;
            if (topSibling) {
                topSibling.insertAdjacentElement('afterend', contentContainer);
            } else {
                this.widgetContainer.append(contentContainer);
            }
        }

        contentContainer.append(...nonSlotChildren);
    }

    private createMiddleContainer() {
        const middleSlotContainer = this.getFormattedSlot(
            UIWidget.slots.Middle
        );
        if (!middleSlotContainer) {
            return;
        }

        if (this.topWrapper) {
            this.topWrapper.insertAdjacentElement(
                'afterend',
                middleSlotContainer
            );
        } else {
            this.widgetContainer.append(middleSlotContainer);
        }
    }

    private createBottomContainer() {
        const bottomContainer = this.getFormattedSlot(UIWidget.slots.Bottom);
        if (bottomContainer) {
            this.widgetContainer.append(bottomContainer);
        }
    }

    private removeEmptyContainers(): void {
        if (this.topWrapper) {
            const hasTopLeftContent = !!this.topLeftWrapper?.children?.length;
            const hasTopRightContent = !!this.topRightWrapper?.children?.length;

            if (!hasTopLeftContent && !hasTopRightContent) {
                this.topWrapper.remove();
            } else if (!hasTopRightContent) {
                this.topRightWrapper?.remove();
            }
        }

        if (this.middleWrapper && !this.middleWrapper.children.length) {
            this.middleWrapper.remove();
        }

        if (this.contentWrapper && !this.contentWrapper.children.length) {
            this.contentWrapper.remove();
        }

        if (this.bottomWrapper && !this.bottomWrapper.children.length) {
            this.bottomWrapper.remove();
        }
    }

    private updateContainers() {
        this.removeEmptyContainers();
        this.createWidgetContainer();
        this.createTopContainer();
        this.createMiddleContainer();
        this.createContentContainer();
        this.createBottomContainer();
    }

    /**
     * Fires callback when component mutates.
     * @param {Array<MutationRecord>} mutations List of mutations that occurred
     */
    handleMutations(mutations: Array<MutationRecord>): void {
        const mutationTargets = [
            this,
            this.widgetContainer,
            this.topLeftWrapper,
            this.topRightWrapper,
            this.middleWrapper,
            this.contentWrapper,
            this.bottomWrapper,
        ].filter(Boolean) as Array<Node>;

        const isWidgetMutation = mutations.some((mutation) =>
            mutationTargets.includes(mutation.target)
        );
        if (isWidgetMutation) {
            this.updateContainers();
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.updateContainers();
    }
}
